import {
  type ParserT,
  Parser,
  field,
  number,
  string,
  oneOf,
  array,
  boolean,
  nullable,
  record,
  date
} from '@freckle/parser'
import {
  type IdentityProviderT,
  parser as identityProviderParser
} from '@frontrow/console/src/entities/helpers/identity-provider'
import {
  type CamelCaseKeysOnly,
  type FancyOk,
  type fancyOperations
} from '@frontrow/console/src/open-api/clients'

export type StarStatus = 'star_enabled' | 'star_disabled' | 'star_unset'

const allStarStatuses: StarStatus[] = ['star_enabled', 'star_disabled', 'star_unset']

export type RenaissanceSchoolClientT = {
  platform: string
  clientId: string
  rpid: string
  baseUrl: string
  starStatus: StarStatus
  sharedRosteringEnabled: boolean
}

export type SchoolAttrs = {
  id: number
  name: string
  createdAt: Date
  updatedAt: Date
  districtId: number
  sisId: string | undefined | null
  ncesSchoolId: string | undefined | null
  cleverSchoolId: string | undefined | null
  classLinkSchoolId: string | undefined | null
  renaissanceRpIdentifier: string | undefined | null
  city: string
  administrativeArea: string | undefined | null
  postalCode: string | undefined | null
  formattedAddressLines: Array<string> | undefined | null
  countryCode: string | undefined | null
  licenseUtilizationNotificationsMutedAt: Date | undefined | null
  courseSubjectsByLicensing: boolean
  renaissanceSchoolClient: RenaissanceSchoolClientT | undefined | null
  activeIdp: IdentityProviderT | undefined | null
  syncIdp: boolean
  crmId: string | undefined | null
  active: boolean // a school can be active/inactive in RL CRM. As that becomes our source-of-truth, we mirror that data.;
  crmAccountNumber: number | undefined | null
  identityProvider: IdentityProviderT | undefined | null
  idpManaged: boolean
}

const parseRenaissanceSchoolClient: ParserT<RenaissanceSchoolClientT> = record({
  platform: string(),
  clientId: string(),
  rpid: string(),
  baseUrl: string(),
  starStatus: oneOf<StarStatus>('StarStatus', allStarStatuses),
  sharedRosteringEnabled: boolean()
})

export const parseSchoolAttrs: ParserT<SchoolAttrs> = record({
  id: number(),
  name: string(),
  createdAt: date(),
  updatedAt: date(),
  districtId: number(),
  sisId: nullable(string()),
  ncesSchoolId: nullable(string()),
  cleverSchoolId: nullable(string()),
  classLinkSchoolId: nullable(string()),
  renaissanceRpIdentifier: field(nullable(string()), 'renaissanceRPIdentifier'),
  city: string(),
  administrativeArea: nullable(string()),
  postalCode: nullable(string()),
  formattedAddressLines: nullable(array(string())),
  countryCode: nullable(string()),
  licenseUtilizationNotificationsMutedAt: nullable(date()),
  courseSubjectsByLicensing: boolean(),
  renaissanceSchoolClient: nullable(parseRenaissanceSchoolClient),
  activeIdp: nullable(identityProviderParser),
  syncIdp: boolean(),
  crmId: nullable(string()),
  active: boolean(),
  crmAccountNumber: nullable(number()),
  identityProvider: nullable(identityProviderParser),
  idpManaged: boolean()
})

// `getDistrictSchoolsR`'s element is the narrowest school response, so every
// caller's body fits it.
type SchoolResponse = CamelCaseKeysOnly<FancyOk<fancyOperations['getDistrictSchoolsR']>[number]>

export function parseSchool(response: SchoolResponse): SchoolAttrs {
  return Parser.run(response, parseSchoolAttrs)
}

export function parseSchools(response: Array<SchoolResponse>): Array<SchoolAttrs> {
  return Parser.run(response, array(parseSchoolAttrs))
}

// Subtype to support DetailedSchoolT, BasicSchoolT, etc used in Console:
type SchoolCrmFieldsT = {
  crmId: string | undefined | null
  active: boolean
}

export function isSchoolCrmSynced(school: SchoolCrmFieldsT): boolean {
  return school.active && school.crmId !== null && school.crmId !== undefined
}
